import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CategoriesProvider } from "@/contexts/CategoriesContext";
import { HouseholdProvider } from "@/contexts/HouseholdContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import SetPassword from "@/pages/SetPassword";
import OAuthConsent from "@/pages/OAuthConsent";
import { peekPostLoginRedirect } from "@/lib/postLoginRedirect";
import Dashboard from "@/pages/Dashboard";
import Expenses from "@/pages/Expenses";
import Subscriptions from "@/pages/Subscriptions";
import SinkingFunds from "@/pages/SinkingFunds";
import FundDetail from "@/pages/FundDetail";
import Budgets from "@/pages/Budgets";
import Tools from "@/pages/Tools";
import Investments from "@/pages/Investments";
import InvestmentDetail from "@/pages/InvestmentDetail";
import Transactions from "@/pages/Transactions";
import CalendarPage from "@/pages/Calendar";
import Analytics from "@/pages/Analytics";
import Pricing from "@/pages/Pricing";
import Settings from "@/pages/Settings";
import MobileMenu from "@/pages/MobileMenu";
import Admin from "@/pages/Admin";
import Contact from "@/pages/Contact";
import Help from "@/pages/Help";
import AppShell from "@/components/AppShell";

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    const loc = useLocation();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse text-slate-500 text-sm">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
    // Google sign-in always returns to /dashboard — resume an AI-connector consent if one is pending
    const resume = peekPostLoginRedirect();
    if (resume) return <Navigate to={resume} replace />;
    return <AppShell>{children}</AppShell>;
}

// Admin routes require both an authenticated session AND role === "admin" — a non-admin
// hitting /admin directly is bounced to the Dashboard rather than shown a 403 page.
function AdminRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
    return <AppShell>{children}</AppShell>;
}

function PublicOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to={peekPostLoginRedirect() || "/dashboard"} replace />;
    return children;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
            {/* Not wrapped in PublicOnly/ProtectedRoute — a Supabase session exists here
                (from the invite/recovery magic link) before the user has set a password,
                and AuthContext may resolve `user` moments later, which would otherwise
                bounce them straight to "/" via PublicOnly before they finish setup. */}
            <Route path="/accept-invite" element={<SetPassword />} />
            <Route path="/reset-password" element={<SetPassword />} />
            {/* AI-connector (MCP) consent — handles its own signed-out redirect, see OAuthConsent.jsx */}
            <Route path="/oauth/consent" element={<OAuthConsent />} />
            {/* The marketing/conversion landing page lives at "/" as a static file served
                directly by nginx (frontend/public/landing.html) — it never reaches this
                router on a real page load. This route only exists as a safety net in case
                some client-side navigation ever points at "/" without a full page reload
                (e.g. a stray Link/navigate("/") we missed); it just bounces into the app. */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
            <Route path="/subscriptions" element={<ProtectedRoute><Subscriptions /></ProtectedRoute>} />
            <Route path="/budgets" element={<ProtectedRoute><Budgets /></ProtectedRoute>} />
            <Route path="/tools" element={<ProtectedRoute><Tools /></ProtectedRoute>} />
            <Route path="/investments" element={<ProtectedRoute><Investments /></ProtectedRoute>} />
            <Route path="/investments/:aid" element={<ProtectedRoute><InvestmentDetail /></ProtectedRoute>} />
            <Route path="/funds" element={<ProtectedRoute><SinkingFunds /></ProtectedRoute>} />
            <Route path="/funds/:fid" element={<ProtectedRoute><FundDetail /></ProtectedRoute>} />
            <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/contact" element={<ProtectedRoute><Contact /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="/menu" element={<ProtectedRoute><MobileMenu /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <div className="App">
            <ThemeProvider>
                <AuthProvider>
                    <CategoriesProvider>
                        <HouseholdProvider>
                            <BrowserRouter>
                                <AppRoutes />
                                <Toaster position="top-right" richColors closeButton />
                            </BrowserRouter>
                        </HouseholdProvider>
                    </CategoriesProvider>
                </AuthProvider>
            </ThemeProvider>
        </div>
    );
}
